const { PRODUCT_TILE_LOCATION } = require('../../../../config/analytics');
const jsdocTypes = require('../../../../jsdoc-types'); // eslint-disable-line no-unused-vars
const { onOnce } = require('../common/utils/domEvents');
const { analyticsEventBus } = require('./events');

/**
 * Returns de B2C Commerce JavaScript library for Active Data Collection
 *
 * <![CDATA[ (head-active_data.js)
 * var dw = (window.dw || {});
 * dw.ac = {
 *     _analytics: null,
 *     _events: [],
 *     _category: "",
 *     _searchData: "",
 *     _anact: "",
 *     _anact_nohit_tag: "",
 *     _analytics_enabled: "false",
 *     _timeZone: "Etc/GMT+8",
 *     _capture: function(configs) {
 *         if (Object.prototype.toString.call(configs) === "[object Array]") {
 *             configs.forEach(captureObject);
 *             return;
 *         }
 *         dw.ac._events.push(configs);
 *     },
 *   capture: function() {
 *     dw.ac._capture(arguments);
 *     // send to CQ as well:
 *     if (window.CQuotient) {
 *       window.CQuotient.trackEventsFromAC(arguments);
 *     }
 *   },
 *     EV_PRD_SEARCHHIT: "searchhit",
 *     EV_PRD_DETAIL: "detail",
 *     EV_PRD_RECOMMENDATION: "recommendation",
 *     EV_PRD_SETPRODUCT: "setproduct",
 *     applyContext: function(context) {
 *         if (typeof context === "object" && context.hasOwnProperty("category")) {
 *           dw.ac._category = context.category;
 *         }
 *         if (typeof context === "object" && context.hasOwnProperty("searchData")) {
 *           dw.ac._searchData = context.searchData;
 *         }
 *     },
 *     setDWAnalytics(analytics) {
 *         dw.ac._analytics = analytics;
 *     },
 *     eventsIsEmpty: function() {
 *         return 0 == dw.ac._events.length;
 *     }
 * };
 *
 * @returns {Object} The window.dw.ac lib
 */
function getLibrary() {
  const { dw } = window;
  if (dw && dw.ac) return dw.ac;
  throw new Error(
    'window.dw.ac is not found. Check whether the <isactivedatahead> tag has rendered.'
  );
}

/**
 * Enum for ACProductData type. The type of event that causes the information
 * captureThese constants correspond to the four values of the view attribute
 * for the <isobject> tag.
 * @readonly
 * @enum {string}
 */
const ACProductDataType = {
  /** A product shown in detail, as for a product detail page */
  EV_PRD_DETAIL: getLibrary().EV_PRD_DETAIL,
  /** A product shown as a recommendation to the customer */
  EV_PRD_RECOMMENDATION: getLibrary().EV_PRD_RECOMMENDATION,
  /** A product shown as a hit in search results */
  EV_PRD_SEARCHHIT: getLibrary().EV_PRD_SEARCHHIT,
  /** A product shown as a constituent of a product set */
  EV_PRD_SETPRODUCT: getLibrary().EV_PRD_SETPRODUCT,
};

/**
 * Object that relates a product tile location to its corresponding EV_PRD to
 * send to Demandware's active data
 */
const ACProductDataTypeByProductTileLocation = {
  [PRODUCT_TILE_LOCATION.cartRecommendedProducts]:
    ACProductDataType.EV_PRD_RECOMMENDATION,
  [PRODUCT_TILE_LOCATION.emptyCartRecommendedProducts]:
    ACProductDataType.EV_PRD_RECOMMENDATION,
  [PRODUCT_TILE_LOCATION.crossSellingGrid]:
    ACProductDataType.EV_PRD_RECOMMENDATION,
  [PRODUCT_TILE_LOCATION.homeShopTheLook]:
    ACProductDataType.EV_PRD_RECOMMENDATION,
  [PRODUCT_TILE_LOCATION.pdpCompleteTheLook]:
    ACProductDataType.EV_PRD_RECOMMENDATION,
  [PRODUCT_TILE_LOCATION.pdpRecommendedProducts]:
    ACProductDataType.EV_PRD_RECOMMENDATION,
  [PRODUCT_TILE_LOCATION.plpGrid]: ACProductDataType.EV_PRD_SEARCHHIT,
  [PRODUCT_TILE_LOCATION.plpSliderBanner]: ACProductDataType.EV_PRD_SEARCHHIT,
  [PRODUCT_TILE_LOCATION.search]: ACProductDataType.EV_PRD_SEARCHHIT,
  [PRODUCT_TILE_LOCATION.searchModal]: ACProductDataType.EV_PRD_RECOMMENDATION,
};

/**
 * @typedef {object} ACProductData
 * @property {string} [id] The ID (SKU) of the product
 * @property {ACProductDataType} type The type of data to collect, such as
 * dw.ac.EV_PRD_SEARCHHIT. The type of event that causes the information capture.
 */

/**
 * Collects the analytics information for products and submits it for processing
 * @param {ACProductData|ACProductData[]} data The data to collect
 * @example capture({ id: pid, type: ACProductDataType.EV_PRD_DETAIL });
 * @see {@link https://documentation.b2c.commercecloud.salesforce.com/DOC1/topic/com.demandware.dochelp/content/b2c_commerce/topics/active_merchandising/b2c_add_java_script_calls_for_dhtml_widgets.html }
 */
function capture(data) {
  getLibrary().capture(data);
}

/**
 * @typedef {object} ACContextConfig
 * @property {string} [category] The ID of the category browsed on the page
 */

/**
 * Function to change the analytics context from what is initially provided by
 * the <isactivedatacontext> tag of ISML. Each time the applyContext function
 * is called, it affects all subsequent calls to capture until the next call to
 * applyContext.
 *
 * For example, assume that a home page has a slot that shows the top selling
 * product in each category in a carousel. Each time the carousel shows a new
 * product, the context for that product is a different category. This function
 * is called each time the carousel is scrolled, to set the appropriate category
 * for the product now shown.
 *
 * @param {ACContextConfig} configs The context
 * @example applyContext({ category: 'SomeCategoryID' });
 * @see {@link https://documentation.b2c.commercecloud.salesforce.com/DOC1/topic/com.demandware.dochelp/content/b2c_commerce/topics/active_merchandising/b2c_add_java_script_calls_for_dhtml_widgets.html }
 */
function applyContext(configs) {
  getLibrary().applyContext(configs);
}

/**
 * The initPDP function is called when the productDetailsViewed event is fired. This event is fired when
 * the product details page is loaded
 */
function init() {
  analyticsEventBus.subscribe(
    analyticsEventBus.EVENTS.productDetailsViewed,
    handleProductView
  );
  analyticsEventBus.subscribe(
    analyticsEventBus.EVENTS.productImpressionViewed,
    handleProductImpressionViewed
  );
  analyticsEventBus.subscribe(
    analyticsEventBus.EVENTS.productListViewed,
    handleProductListView
  );

  onOnce(window, 'beforeunload', () => {
    analyticsEventBus.unsubscribe(
      analyticsEventBus.EVENTS.productDetailsViewed,
      handleProductView
    );
    analyticsEventBus.unsubscribe(
      analyticsEventBus.EVENTS.productImpressionViewed,
      handleProductImpressionViewed
    );
    analyticsEventBus.unsubscribe(
      analyticsEventBus.EVENTS.productListViewed,
      handleProductListView
    );
  });
}

/**
 * It tracks a product details view
 * @param {Object} data - Event data object
 * @param {FullProduct} data.product - The product
 */
function handleProductView(data) {
  capture({ id: data.product.id, type: ACProductDataType.EV_PRD_DETAIL });
}

/**
 * It tracks a viewed product impression
 * @param {Object} payload The payload of the event
 * @param {string} payload.location The location where the tile is viewed
 * @param {string} payload.position The product position
 * @param {Types.FullProduct} payload.product The just-viewed product
 */
function handleProductImpressionViewed(payload) {
  const type =
    ACProductDataTypeByProductTileLocation[payload.location] ??
    ACProductDataType.EV_PRD_SEARCHHIT;

  capture({
    id: payload.product.id,
    type,
  });
}

/**
 * It tracks a product list view
 * @param {Object} data - The payload of the event
 * @param {Object} payload.category Current category
 * @param {Object[]} payload.products Products listed
 * @param {Object} payload.products.display Display config for a product
 * @param {jsdocTypes.FullProduct} payload.products.product Products listed
 * @param {Object} payload.products.urls URLS of the product
 */
function handleProductListView(data) {
  /* eslint-disable prefer-destructuring */
  /** @type {Category} category */
  const category = data.category;
  /* eslint-enable prefer-destructuring */
  applyContext({ category: category.id });
}

module.exports = {
  init,
};
